import { Injectable, inject } from "@angular/core";
import {
    Observable,
    Subject,
    firstValueFrom,
    from,
    map,
    tap
} from "rxjs";
import { GetVirkeskopsKontraktBilagorResponseDTO } from "../../../common/models/virkeskop/v2/GetVirkeskopsKontraktBilagorResponseDTO";
import { SaveVirkeskopsKontraktBilagaRequestDTO } from "../../../common/models/virkeskop/v2/SaveVirkeskopsKontraktBilagaRequestDTO";
import { SaveVirkeskopsKontraktBilagaResponseDTO } from "../../../common/models/virkeskop/v2/SaveVirkeskopsKontraktBilagaResponseDTO";
import { ApiClientService } from "../../../common/services/api-client.service";
import { UpdateKontraktBilagaCommand } from "../commands/bilaga/UpdateKontraktBilagaCommand";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktDokumentService } from "./kontrakt-dokument.service";

@Injectable()
export class KontraktBilagorService {
    readonly reloadBilagorSrc = new Subject<void>();

    private readonly apiClient = inject(ApiClientService);
    private readonly bilagaChangeSrc = new Subject<void>();
    private readonly kontraktCommandService = inject(KontraktCommandService);
    private readonly kontraktDokumentService = inject(KontraktDokumentService);

    get bilagaChange$() {
        return this.bilagaChangeSrc.asObservable();
    }

    getStreamForBilaga(kontraktId: number, bilagaId: number): Observable<Blob> {
        return this.kontraktDokumentService.getDokumentForKontraktDownload(kontraktId, bilagaId
        );
    }

    async deleteBilaga(bilagaId: number): Promise<boolean> {
        const delete$ = this.apiClient.delete(`virkeskopsbilaga/${bilagaId}`).pipe(
            tap(() => this.bilagaChangeSrc.next())
        );
        return firstValueFrom(delete$);
    }

    getBilagor(
        kontraktId: number
    ): Observable<GetVirkeskopsKontraktBilagorResponseDTO> {
        return from(
            this.kontraktDokumentService.getDokumentForKontrakt(
                kontraktId,
                "Kontrakt-bilaga"
            )
        ).pipe(
            map((dokument) => {
                const bilagor = dokument.map((d) => ({
                    fileId: d.id,
                    fileName: d.filnamn,
                    fileDate: d.skapadTidpunkt,
                    fileVersion: d.version,
                    synligForKopplanera: d.synligForKopplanera ?? false
                }));
                return { bilagor };
            })
        );
    }

    updateBilaga(dokumentId: number, synligForKopplanera: boolean) {
        const command = new UpdateKontraktBilagaCommand(
            dokumentId,
            synligForKopplanera
        );
        this.kontraktCommandService.update(command);
    }

    async uploadBilaga(bilaga: SaveVirkeskopsKontraktBilagaRequestDTO): Promise<SaveVirkeskopsKontraktBilagaResponseDTO> {
        const upload$ = this.apiClient
            .post<SaveVirkeskopsKontraktBilagaRequestDTO, SaveVirkeskopsKontraktBilagaResponseDTO>("virkeskopsbilaga", bilaga)
            .pipe(tap(() => this.bilagaChangeSrc.next()));
        return firstValueFrom(upload$);
    }
}
